<script setup lang="ts">
import { computed, toRef } from 'vue'
import { withoutKeys } from '@/helpers'
import type { ZodSchema } from 'zod'
import { useUnifiiField } from '@/composables/inputs'

// Prevent attribute inheritance.
defineOptions({ inheritAttrs: false })

export type InputNumberProps = Partial<{
  name: string
  value: string | null | number
  rules: ZodSchema
  inline: boolean
  required: boolean
  step: number
  errorVariant: 'tooltip' | 'inline'
  align: 'left' | 'right'
  error: string
  numericOnly: boolean
}>

const props = withDefaults(defineProps<InputNumberProps>(), {
  name: '',
  value: '',
  step: 0.1,
  errorVariant: 'inline',
  align: 'left',
  numericOnly: false
})

const $emit = defineEmits<{ 'update:value': [number | undefined] }>()

const {
  errorMessage,
  value: currentValue,
  handleBlur,
  handleChange,
  handleInput
} = useUnifiiField({
  name: toRef(props, 'name'),
  rules: toRef(props, 'rules'),
  initialValue: toRef(props, 'value'),
  emitter: (value: number | undefined) => $emit('update:value', value)
})
const errorForDisplay = computed(() => errorMessage.value ?? props.error)

function sanitiseNumericOnlyInput(raw: string) {
  let v = raw.replace(/,/g, '.')
  v = v.replace(/[^\d.]/g, '')

  const dotIndex = v.indexOf('.')
  if (dotIndex !== -1) {
    v = v.slice(0, dotIndex + 1) + v.slice(dotIndex + 1).replace(/\./g, '')
  }

  if (v.startsWith('.')) v = `0${v}`

  return v
}

function handleNumericOnlyInput(e: Event) {
  if (props.numericOnly && e.target instanceof HTMLInputElement) {
    const raw = e.target.value
    const cleaned = sanitiseNumericOnlyInput(raw)

    if (cleaned !== raw) {
      e.target.value = cleaned

      if (cleaned === '' && raw !== '') {
        return
      }
    }
  }

  handleInput(e)
}
</script>

<template>
  <div :class="{ 'd-inline-block': inline, 'd-block': !inline }">
    <div
      class="position-relative flex-nowrap"
      :class="[
        {
          'input-group': 'prefix' in $slots || 'suffix' in $slots,
          'has-validation': errorForDisplay,
          [$style['required']]: required
        },
        $style['input-number'],
        $attrs.class
      ]">
      <span class="input-group-text" v-if="'prefix' in $slots"><slot name="prefix" /></span>
      <input
        v-bind="withoutKeys($attrs, ['class'])"
        v-tooltip="
          errorVariant === 'tooltip' ? { text: errorForDisplay, variant: 'error' } : undefined
        "
        inputmode="numeric"
        type="text"
        :name="name"
        :value="currentValue"
        class="form-control"
        :class="{
          'is-invalid': errorForDisplay,
          'text-end': align === 'right'
        }"
        @change="handleChange"
        @blur="handleBlur"
        @input="handleNumericOnlyInput" />
      <div class="invalid-tooltip" v-if="errorVariant === 'inline' && errorForDisplay">
        {{ errorForDisplay }}
      </div>
      <span class="input-group-text" v-if="'suffix' in $slots"><slot name="suffix" /></span>
    </div>
  </div>
</template>

<style module lang="scss">
@use '@/assets/styles/mixins-inputs';
@use '@/assets/mixins/inputs';

.input-number {
  input {
    appearance: textfield;

    // Hide number spinner
    &::-webkit-inner-spin-button {
      display: none;
    }
  }

  @include mixins-inputs.fix-error-tooltips-for-inputs;
  @include inputs.apply-prefix-and-suffix-styles;
}

.required {
  @include inputs.apply-required-indicator-styles;
}
</style>
